import { useEffect, useReducer } from 'react';

type AsyncResourceState<T> =
  | { status: 'idle' | 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; error: string };

type AsyncResourceAction<T> =
  | { type: 'LOAD' }
  | { type: 'READY'; data: T }
  | { type: 'ERROR'; error: string };

function asyncResourceReducer<T>(
  state: AsyncResourceState<T>,
  action: AsyncResourceAction<T>,
): AsyncResourceState<T> {
  switch (action.type) {
    case 'LOAD':
      return { status: 'loading' };
    case 'READY':
      return { status: 'ready', data: action.data };
    case 'ERROR':
      return { status: 'error', error: action.error };
    default:
      return state;
  }
}

export function useAsyncBlockResource<T>(
  cacheKey: string,
  loader: () => Promise<T>,
  version?: number,
): AsyncResourceState<T> {
  const [state, dispatch] = useReducer(asyncResourceReducer<T>, { status: 'idle' });

  useEffect(() => {
    let cancelled = false;

    dispatch({ type: 'LOAD' });

    loader()
      .then((data) => {
        if (!cancelled) {
          dispatch({ type: 'READY', data });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : String(err);
          dispatch({ type: 'ERROR', error: message });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, version]);

  return state;
}
